/**
 * หน่วยเงินและการปัดเศษ — จุดเดียวในระบบที่แตะเรื่องนี้ได้
 *
 * ทุกจำนวนเงินเป็น "สตางค์" แบบจำนวนเต็ม
 *   1 บาท     = 100
 *   1,205 บาท = 120500
 *
 * ทำไมไม่ใช้ branded type: การบวกลบเลข branded ใน TypeScript ต้องห่อฟังก์ชัน
 * ทุกครั้ง ซึ่งทำให้โค้ดอ่านยากกว่าที่ได้ประโยชน์คืนมา เราใช้วิธี
 *   1) ตั้งชื่อพารามิเตอร์ลงท้าย Satang เสมอ
 *   2) assertSatang() ตรวจตอนรันจริงว่าเป็นจำนวนเต็ม
 *   3) test ครอบทุกสูตร
 * แทน
 */

/** ค่าคลาดเคลื่อนที่ยอมรับได้ตอนปัดเศษ — กัน 10.000000001 ถูกปัดขึ้นเป็น 11 */
const EPSILON = 1e-9;

export const SATANG_PER_BAHT = 100;

/** แปลงบาทเป็นสตางค์ · รับได้ทั้ง 150 และ 150.25 */
export function fromBaht(baht: number): number {
  return Math.round(baht * SATANG_PER_BAHT);
}

/** แปลงสตางค์เป็นบาท (ทศนิยม 2 ตำแหน่ง) — ใช้ตอนแสดงผลเท่านั้น */
export function toBaht(satang: number): number {
  return satang / SATANG_PER_BAHT;
}

/** จัดรูปแบบสำหรับแสดงผล เช่น "1,144.00" */
export function formatBaht(satang: number, decimals = 2): string {
  return toBaht(satang).toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** ตรวจว่าเป็นจำนวนสตางค์ที่ถูกต้อง — จำนวนเต็มและมีค่าจริง */
export function assertSatang(value: number, label = 'value'): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${label} ต้องเป็นตัวเลข แต่ได้ ${value}`);
  }
  if (!Number.isInteger(value)) {
    throw new Error(`${label} ต้องเป็นจำนวนเต็มสตางค์ แต่ได้ ${value}`);
  }
}

/**
 * ปัดขึ้นเป็นบาทเต็ม
 *
 * ข้อตกลงกับเจ้าของร้าน: ส่วนลดที่มีเศษสตางค์ให้ปัดขึ้นเป็นบาทเต็มเสมอ
 *   750    (7.50 บาท)  → 800    (8 บาท)
 *   6025   (60.25 บาท) → 6100   (61 บาท)
 *   1000   (10 บาท)    → 1000   (10 บาท)  ← ลงตัวอยู่แล้ว ห้ามปัดเพิ่ม
 */
export function ceilToBaht(satang: number): number {
  const result = Math.ceil(satang / SATANG_PER_BAHT - EPSILON) * SATANG_PER_BAHT;
  // การลบ EPSILON ทำให้ 0 กลายเป็น -0 ซึ่งเป็นค่าที่หลุดไปสร้างปัญหาได้
  // ตอนแปลงเป็น JSON หรือเทียบด้วย Object.is — ปรับให้เป็น 0 ปกติ
  return result === 0 ? 0 : result;
}

/**
 * คำนวณเปอร์เซ็นต์ของยอด — ยังไม่ปัด
 *
 * คูณด้วยเปอร์เซ็นต์ที่ขยายเป็นจำนวนเต็มก่อน (5.25% → 525) เพื่อลด
 * ความคลาดเคลื่อนของทศนิยมให้เหลือน้อยที่สุด
 */
export function percentOf(baseSatang: number, percent: number): number {
  assertSatang(baseSatang, 'baseSatang');
  if (percent < 0 || percent > 100) {
    throw new Error(`percent ต้องอยู่ระหว่าง 0-100 แต่ได้ ${percent}`);
  }
  const scaled = Math.round(percent * 100); // ทศนิยม 2 ตำแหน่งของเปอร์เซ็นต์
  return (baseSatang * scaled) / 10_000;
}

/** รวมยอดแบบปลอดภัย — ตรวจทุกตัวว่าเป็นจำนวนเต็มสตางค์ */
export function sumSatang(values: readonly number[], label = 'amount'): number {
  let total = 0;
  for (const v of values) {
    assertSatang(v, label);
    total += v;
  }
  return total;
}

/** หารแล้วคืนทศนิยม 2 ตำแหน่ง — ใช้กับค่าเฉลี่ยในรายงาน ไม่ตัดเศษทิ้ง */
export function averageBaht(totalSatang: number, count: number): number {
  if (count === 0) return 0;
  return Math.round((totalSatang / count / SATANG_PER_BAHT) * 100) / 100;
}
